import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useDebouncedQuery } from '../../hooks/useDebouncedQuery';
import { rateLimiter } from '../../utils/rateLimiter';
import { apiRateLimiter } from '../../utils/apiRateLimiter';
import { Link, useNavigate } from 'react-router-dom';
import { BarChart3, Users, Settings, TrendingUp, DollarSign, Package, ShoppingCart, Clock, Calendar, Wrench, LogOut, MapPin, UserCog, Building, ChevronDown, Save, Phone, Mail, Globe, CreditCard, Truck, Monitor, FileText, Calculator, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardAPI, sitesAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

interface DashboardStats {
  totalRevenue: number;
  totalOrders: number;
  totalCustomers: number;
  activeOrders: number;
  pendingPayments: number;
  todayRevenue: number;
  weeklyGrowth: number;
  expensesThisMonth?: number;
  netProfit?: number;
}

interface RecentOrder {
  _id: string;
  orderNumber: string;
  customerId: {
    firstName: string;
    lastName: string;
  };
  totalAmount: number;
  status: string;
  createdAt: string;
}

interface CustomerStats {
  newCustomers30Days: number;
  activeCustomers: number;
}

interface RevenueReport {
  thisMonth: number;
  lastMonth: number;
  growth: number;
  expensesThisMonth?: number;
  expensesLastMonth?: number;
  netThisMonth?: number;
  netLastMonth?: number;
  netGrowth?: number;
}

interface PopularService {
  name: string;
  orderCount: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, activeSiteId, activeSiteName, setActiveSite } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalRevenue: 0,
    totalOrders: 0,
    totalCustomers: 0,
    activeOrders: 0,
    pendingPayments: 0,
    todayRevenue: 0,
    weeklyGrowth: 0,
  });
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [customerStats, setCustomerStats] = useState<CustomerStats>({ newCustomers30Days: 0, activeCustomers: 0 });
  const [revenueReport, setRevenueReport] = useState<RevenueReport>({ thisMonth: 0, lastMonth: 0, growth: 0 });
  const [popularServices, setPopularServices] = useState<PopularService[]>([]);
  const [revenueChartData, setRevenueChartData] = useState<{ date: string; revenue: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('overview');
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
  const ordersPerPage = 10;

  const totalOrderPages = Math.ceil(recentOrders.length / ordersPerPage);
  const paginatedOrders = recentOrders.slice(
    (ordersCurrentPage - 1) * ordersPerPage,
    ordersCurrentPage * ordersPerPage
  );

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sites, setSites] = useState<{ _id: string; name: string; code: string }[]>([]);
  const [showSiteSelector, setShowSiteSelector] = useState(false);
  const [siteData, setSiteData] = useState<any>(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Fetch site data when settings tab is activated
  useEffect(() => {
    if (activeTab === 'settings' && activeSiteId) {
      const fetchSite = async () => {
        setSiteLoading(true);
        try {
          const res = await sitesAPI.getSite(activeSiteId);
          setSiteData(res.data);
        } catch {
          toast.error('Failed to load site settings');
        } finally {
          setSiteLoading(false);
        }
      };
      fetchSite();
    }
  }, [activeTab, activeSiteId]);

  const handleSaveSettings = async () => {
    if (!siteData || !activeSiteId) return;
    setSavingSettings(true);
    try {
      await sitesAPI.updateSite(activeSiteId, siteData);
      toast.success('Settings saved successfully');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const updateSiteField = (path: string, value: any) => {
    setSiteData((prev: any) => {
      if (!prev) return prev;
      const updated = { ...prev };
      const keys = path.split('.');
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  // Fetch sites list for super_admin
  useEffect(() => {
    if (user?.role === 'super_admin') {
      const fetchSites = async () => {
        try {
          console.log('Fetching sites for super admin...');
          const token = localStorage.getItem('token');
          const res = await fetch('/api/sites', {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          console.log('Sites API response:', data);
          // Handle new API response format { sites: [...] }
          const sitesArray = Array.isArray(data) ? data : data.sites || [];
          console.log('Sites array:', sitesArray);
          console.log('Sites array length:', sitesArray.length);
          setSites(sitesArray);
        } catch (error) {
          console.error('Failed to fetch sites:', error);
        }
      };
      fetchSites();
    }
  }, [user?.role]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!activeSiteId) {
        console.log('No active site, skipping dashboard data fetch');
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching dashboard data for site:', activeSiteId);
        
        // Use rate-limited API calls to prevent excessive requests
        const [statsRes, ordersRes, customerStatsRes, revenueRes, servicesRes, chartDataRes] = await Promise.all([
          apiRateLimiter.debounce('dashboard-stats', () => dashboardAPI.getStats(), 500),
          apiRateLimiter.debounce('dashboard-orders', () => dashboardAPI.getRecentOrders(), 600),
          apiRateLimiter.debounce('dashboard-customers', () => dashboardAPI.getCustomerStats(), 700),
          apiRateLimiter.debounce('dashboard-revenue', () => dashboardAPI.getRevenueReport(), 800),
          apiRateLimiter.debounce('dashboard-services', () => dashboardAPI.getPopularServices(), 900),
          apiRateLimiter.debounce('dashboard-chart', () => dashboardAPI.getRevenueChartData(), 1000)
        ]);
        
        setStats(statsRes?.data);
        setRecentOrders(ordersRes?.data);
        setCustomerStats(customerStatsRes?.data);
        setRevenueReport(revenueRes?.data);
        setPopularServices(servicesRes?.data);
        setRevenueChartData(chartDataRes?.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    // Add debouncing to prevent rapid successive calls
    const timeoutId = setTimeout(() => {
      if (activeSiteId) {
        fetchDashboardData();
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [activeSiteId]); // Re-fetch when active site changes

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 pt-4 pb-4 mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Top Row: Logo, Sites, User Menu */}
          <div className="flex items-center justify-between mb-4">
            {/* Left: Logo and Site Selector */}
            <div className="flex items-center space-x-3">
              <img src="/logo.jpeg" alt="Logo" className="h-14 w-auto rounded" />
              {user?.role === 'super_admin' ? (
                <div className="relative">
                  <button
                    onClick={() => setShowSiteSelector(!showSiteSelector)}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors cursor-pointer"
                  >
                    <Building className="w-4 h-4 mr-1" />
                    {activeSiteName || 'All Sites'}
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </button>
                  {showSiteSelector && (
                    <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <button
                        onClick={() => {
                          setActiveSite(null, null);
                          setShowSiteSelector(false);
                          toast.success('Viewing all sites');
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${!activeSiteId ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'}`}
                      >
                        All Sites (Global View)
                      </button>
                      {sites.map((site) => (
                        <button
                          key={site._id}
                          onClick={() => {
                            setActiveSite(site._id, site.name);
                            setShowSiteSelector(false);
                            toast.success(`Switched to ${site.name}`);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${activeSiteId === site._id ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'}`}
                        >
                          {site.name} ({site.code})
                        </button>
                      ))}
                      {sites.length === 0 && (
                        <div className="w-full text-left px-4 py-2 text-sm text-gray-500">
                          No sites found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : user?.siteName ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <MapPin className="w-4 h-4 mr-1" />
                  {user.siteName}
                </span>
              ) : null}
            </div>

            {/* Right: User Menu */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Users className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {user?.firstName} {user?.lastName}
                  </span>
                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                    {user?.role}
                  </span>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                      <p className="text-xs text-gray-400">Role: {user?.role}</p>
                    </div>
                    <button
                      onClick={() => {
                        logout();
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions Row */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Link
              to="/admin/expenses"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
            >
              <CreditCard className="h-4 w-4 mr-2 text-gray-400" />
              Expenses
            </Link>

            <Link
              to="/admin/inventory"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
            >
              <Package className="h-4 w-4 mr-2 text-gray-400" />
              Inventory
            </Link>

            <Link
              to="/admin/financial-reports"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
            >
              <BarChart3 className="h-4 w-4 mr-2 text-gray-400" />
              Financial Reports
            </Link>

            {user?.role === 'super_admin' && (
              <Link
                to="/admin/api-usage"
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
              >
                <Activity className="h-4 w-4 mr-2 text-gray-400" />
                API Usage
              </Link>
            )}

            <Link
              to="/pos"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
            >
              <Monitor className="h-5 w-5 mr-2" />
              Launch POS
            </Link>
          </div>

          {/* Bottom Row: Tabs */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${activeTab === 'overview'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              <BarChart3 className="h-5 w-5 mr-2" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${activeTab === 'orders'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              Orders
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${activeTab === 'customers'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              <Users className="h-5 w-5 mr-2" />
              Customers
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${activeTab === 'reports'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              <BarChart3 className="h-5 w-5 mr-2" />
              Reports
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${activeTab === 'settings'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
                }`}
            >
              <Settings className="h-5 w-5 mr-2" />
              Settings
            </button>
            <Link
              to="/admin/services"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            >
              <Wrench className="h-5 w-5 mr-2" />
              Services
            </Link>
            <Link
              to="/admin/billing"
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            >
              <FileText className="h-5 w-5 mr-2" />
              Billing
            </Link>
            {user?.role === 'super_admin' && (
              <>
                <Link
                  to="/admin/sites"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                  <MapPin className="h-5 w-5 mr-2" />
                  Sites
                </Link>
                <Link
                  to="/admin/users"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                  <UserCog className="h-5 w-5 mr-2" />
                  Users
                </Link>
                <Link
                  to="/admin/modules"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                  <Settings className="h-5 w-5 mr-2" />
                  Modules
                </Link>
                <Link
                  to="/admin/pricing"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                  <Calculator className="h-5 w-5 mr-2" />
                  Pricing
                </Link>
                <Link
                  to="/admin/invoices"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                  <FileText className="h-5 w-5 mr-2" />
                  Invoices
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Content */}
        <div>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {/* Revenue Card */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-primary-100 rounded-full p-3">
                          <DollarSign className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-sm font-medium text-gray-900">Total Revenue</h3>
                          <p className="text-2xl font-bold text-primary-600">£{stats.totalRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expenses Card */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-red-100 rounded-full p-3">
                          <CreditCard className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-sm font-medium text-gray-900">Total Expenses</h3>
                          <p className="text-2xl font-bold text-red-600">£{(stats.expensesThisMonth || 0).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Net Profit Card */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-green-100 rounded-full p-3">
                          <BarChart3 className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-sm font-medium text-gray-900">Net Profit</h3>
                          <p className="text-2xl font-bold text-green-600">£{(stats.netProfit || 0).toFixed(2)}</p>
                          <p className="text-xs text-gray-500 mt-1">{stats.weeklyGrowth > 0 ? '+' : ''}{stats.weeklyGrowth}% from last month</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Orders Card */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-primary-100 rounded-full p-3">
                          <ShoppingCart className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-medium text-gray-900">Total Orders</h3>
                          <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
                          <p className="text-sm text-gray-500">{stats.activeOrders} active</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customers Card */}
                  <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-primary-100 rounded-full p-3">
                          <Users className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-medium text-gray-900">Total Customers</h3>
                          <p className="text-2xl font-bold text-gray-900">{stats.totalCustomers}</p>
                          <p className="text-sm text-green-600">+{customerStats.newCustomers30Days} this month</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Today's Performance */}
                  <div className="bg-white overflow-hidden shadow rounded-lg md:col-span-2">
                    <div className="p-6">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 bg-primary-100 rounded-full p-3">
                          <TrendingUp className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-medium text-gray-900">Today's Revenue</h3>
                          <p className="text-2xl font-bold text-primary-600">£{stats.todayRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'orders' && (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Orders</h3>
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedOrders.map((order) => (
                            <tr key={order._id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/orders/${order._id}`)}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.orderNumber}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {order.customerId.firstName} {order.customerId.lastName}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£{order.totalAmount.toFixed(2)}</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${order.status === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : order.status === 'processing'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-gray-100 text-gray-800'
                                    }`}
                                >
                                  {order.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(order.createdAt).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls for Orders */}
                    {recentOrders.length > 0 && (
                      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 mt-4 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                          <button
                            onClick={() => setOrdersCurrentPage(Math.max(1, ordersCurrentPage - 1))}
                            disabled={ordersCurrentPage === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setOrdersCurrentPage(Math.min(totalOrderPages, ordersCurrentPage + 1))}
                            disabled={ordersCurrentPage === totalOrderPages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                          >
                            Next
                          </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm text-gray-700">
                              Showing <span className="font-medium">{(ordersCurrentPage - 1) * ordersPerPage + 1}</span> to <span className="font-medium">{Math.min(ordersCurrentPage * ordersPerPage, recentOrders.length)}</span> of <span className="font-medium">{recentOrders.length}</span> orders
                            </p>
                          </div>
                          <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                              <button
                                onClick={() => setOrdersCurrentPage(Math.max(1, ordersCurrentPage - 1))}
                                disabled={ordersCurrentPage === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                              >
                                <span className="sr-only">Previous</span>
                                <span aria-hidden="true">&larr;</span>
                              </button>
                              {/* Page Numbers */}
                              {[...Array(totalOrderPages)].map((_, i) => (
                                <button
                                  key={i + 1}
                                  onClick={() => setOrdersCurrentPage(i + 1)}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${ordersCurrentPage === i + 1
                                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                    }`}
                                >
                                  {i + 1}
                                </button>
                              ))}
                              <button
                                onClick={() => setOrdersCurrentPage(Math.min(totalOrderPages, ordersCurrentPage + 1))}
                                disabled={ordersCurrentPage === totalOrderPages}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                              >
                                <span className="sr-only">Next</span>
                                <span aria-hidden="true">&rarr;</span>
                              </button>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'customers' && (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center justify-between">
                      Customer Management
                      <Link to="/admin/customers" className="text-sm font-normal text-primary-600 hover:text-primary-700">View All →</Link>
                    </h3>
                    <div className="grid grid-cols-1 gap-6">
                      <div className="bg-white p-6 shadow rounded-lg">
                        <h4 className="text-md font-medium text-gray-900 mb-2">Customer Statistics</h4>
                        <div className="space-y-4">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">New Customers (30 days)</span>
                            <span className="text-2xl font-bold text-green-600">+{customerStats.newCustomers30Days}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Active Customers</span>
                            <span className="text-2xl font-bold text-primary-600">{customerStats.activeCustomers}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'reports' && (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Business Reports</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white p-6 shadow rounded-lg">
                        <h4 className="text-md font-medium text-gray-900 mb-4">Financial Summary</h4>

                        <div className="space-y-4">
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2 border-b pb-1">Revenue</h5>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">This Month</span>
                              <span className="text-sm font-bold text-primary-600">£{revenueReport.thisMonth?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Last Month</span>
                              <span className="text-sm font-medium text-gray-900">£{revenueReport.lastMonth?.toFixed(2) || '0.00'}</span>
                            </div>
                          </div>

                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2 border-b pb-1">Expenses</h5>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">This Month</span>
                              <span className="text-sm font-bold text-red-600">£{revenueReport.expensesThisMonth?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Last Month</span>
                              <span className="text-sm font-medium text-gray-900">£{revenueReport.expensesLastMonth?.toFixed(2) || '0.00'}</span>
                            </div>
                          </div>

                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2 border-b pb-1">Net Profit</h5>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">This Month</span>
                              <span className="text-lg font-bold text-green-600">£{revenueReport.netThisMonth?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Last Month</span>
                              <span className="text-sm font-medium text-gray-900">£{revenueReport.netLastMonth?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between mt-1">
                              <span className="text-sm text-gray-500">Growth</span>
                              <span className={`text-sm font-bold ${(revenueReport.netGrowth || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(revenueReport.netGrowth || 0) > 0 ? '+' : ''}{revenueReport.netGrowth || 0}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white p-6 shadow rounded-lg">
                        <h4 className="text-md font-medium text-gray-900 mb-2">Popular Services</h4>
                        <div className="space-y-2">
                          {popularServices.map((service, index) => (
                            <div key={index} className="flex justify-between">
                              <span className="text-sm text-gray-500">{service.name}</span>
                              <span className="text-2xl font-bold text-primary-600">{service.orderCount} orders</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Revenue Trend Chart */}
                  <div className="bg-white p-6 shadow rounded-lg mb-6">
                    <h4 className="text-md font-medium text-gray-900 mb-6">Revenue Trend (Last 30 Days)</h4>
                    <div className="h-80 w-full">
                      {revenueChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={revenueChartData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                            <XAxis
                              dataKey="date"
                              tickFormatter={(dateStr) => {
                                const date = new Date(dateStr);
                                return `${date.getDate()} ${date.toLocaleString('default', { month: 'short' })}`;
                              }}
                              stroke="#6B7280"
                              fontSize={12}
                              axisLine={false}
                              tickLine={false}
                              dy={10}
                            />
                            <YAxis
                              tickFormatter={(value) => `£${value}`}
                              stroke="#6B7280"
                              fontSize={12}
                              axisLine={false}
                              tickLine={false}
                              dx={-10}
                            />
                            <Tooltip
                              formatter={(value: number) => [`£${value.toFixed(2)}`, 'Revenue']}
                              labelFormatter={(label) => new Date(label).toLocaleDateString()}
                              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px' }} />
                            <Line
                              type="monotone"
                              dataKey="revenue"
                              stroke="#4F46E5"
                              strokeWidth={3}
                              dot={{ r: 4, fill: '#4F46E5', strokeWidth: 0 }}
                              activeDot={{ r: 6, strokeWidth: 0 }}
                              name="Revenue"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          No revenue data available for this period.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6">
                  {siteLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                    </div>
                  ) : !siteData ? (
                    <div className="bg-white shadow rounded-lg p-8 text-center">
                      <p className="text-gray-500">No site selected. Please select a site to manage settings.</p>
                    </div>
                  ) : (
                    <>
                      {/* Save button header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Site Settings</h3>
                          <p className="text-sm text-gray-500">Manage your site configuration for <span className="font-medium text-gray-700">{siteData.name}</span></p>
                        </div>
                        <button
                          onClick={handleSaveSettings}
                          disabled={savingSettings}
                          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {savingSettings ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>

                      {/* Business Information */}
                      <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                            <Building className="w-5 h-5 text-gray-400" /> Business Information
                          </h4>
                        </div>
                        <div className="px-5 py-5">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                              <input
                                type="text"
                                value={siteData.name || ''}
                                onChange={(e) => updateSiteField('name', e.target.value)}
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Site Code</label>
                              <input
                                type="text"
                                value={siteData.code || ''}
                                onChange={(e) => updateSiteField('code', e.target.value.toUpperCase())}
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-gray-50"
                                readOnly
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                              <div className="relative">
                                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <input
                                  type="email"
                                  value={siteData.email || ''}
                                  onChange={(e) => updateSiteField('email', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 pl-9 pr-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                              <div className="relative">
                                <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <input
                                  type="tel"
                                  value={siteData.phone || ''}
                                  onChange={(e) => updateSiteField('phone', e.target.value)}
                                  className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 pl-9 pr-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-gray-400" /> Address
                          </h4>
                        </div>
                        <div className="px-5 py-5">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-3">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                              <input
                                type="text"
                                value={siteData.address || ''}
                                onChange={(e) => updateSiteField('address', e.target.value)}
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                              <input
                                type="text"
                                value={siteData.city || ''}
                                onChange={(e) => updateSiteField('city', e.target.value)}
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                              <input
                                type="text"
                                value={siteData.postcode || ''}
                                onChange={(e) => updateSiteField('postcode', e.target.value)}
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                              <select
                                value={siteData.timezone || 'Europe/London'}
                                onChange={(e) => updateSiteField('timezone', e.target.value)}
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              >
                                <option value="Europe/London">Europe/London (GMT)</option>
                                <option value="Europe/Paris">Europe/Paris (CET)</option>
                                <option value="America/New_York">America/New York (EST)</option>
                                <option value="America/Los_Angeles">America/Los Angeles (PST)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Opening Hours */}
                      <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-gray-400" /> Opening Hours
                          </h4>
                        </div>
                        <div className="px-5 py-5">
                          <div className="space-y-3">
                            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                              const dayData = siteData.openingHours?.[day] || { open: '08:00', close: '18:00', closed: false };
                              return (
                                <div key={day} className="flex items-center gap-4">
                                  <span className="text-sm font-medium text-gray-700 w-24 capitalize">{day}</span>
                                  <label className="flex items-center gap-2 min-w-[80px]">
                                    <input
                                      type="checkbox"
                                      checked={!dayData.closed}
                                      onChange={(e) => updateSiteField(`openingHours.${day}.closed`, !e.target.checked)}
                                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-xs text-gray-500">{dayData.closed ? 'Closed' : 'Open'}</span>
                                  </label>
                                  {!dayData.closed && (
                                    <>
                                      <input
                                        type="time"
                                        value={dayData.open || '08:00'}
                                        onChange={(e) => updateSiteField(`openingHours.${day}.open`, e.target.value)}
                                        className="border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                                      />
                                      <span className="text-sm text-gray-400">to</span>
                                      <input
                                        type="time"
                                        value={dayData.close || '18:00'}
                                        onChange={(e) => updateSiteField(`openingHours.${day}.close`, e.target.value)}
                                        className="border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                                      />
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Tax & Currency */}
                      <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                            <Globe className="w-5 h-5 text-gray-400" /> Tax & Currency
                          </h4>
                        </div>
                        <div className="px-5 py-5">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">VAT Rate (%)</label>
                              <input
                                type="number"
                                value={Math.round((siteData.vatRate || 0) * 100)}
                                onChange={(e) => updateSiteField('vatRate', parseFloat(e.target.value) / 100)}
                                step="0.5"
                                min="0"
                                max="100"
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                              <select
                                value={siteData.currency || 'GBP'}
                                onChange={(e) => updateSiteField('currency', e.target.value)}
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              >
                                <option value="GBP">£ GBP (British Pound)</option>
                                <option value="EUR">€ EUR (Euro)</option>
                                <option value="USD">$ USD (US Dollar)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* POS & Payment Settings */}
                      <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-gray-400" /> POS & Payment Settings
                          </h4>
                        </div>
                        <div className="px-5 py-5">
                          <div className="space-y-4">
                            {[
                              { key: 'allowCashPayments', label: 'Allow Cash Payments' },
                              { key: 'allowCardPayments', label: 'Allow Card Payments' },
                              { key: 'allowAccountPayments', label: 'Allow Account Payments' },
                              { key: 'requireCustomerPhone', label: 'Require Customer Phone' },
                              { key: 'autoPrintReceipts', label: 'Auto-Print Receipts' },
                              { key: 'enableLoyaltyProgram', label: 'Enable Loyalty Program' },
                            ].map(({ key, label }) => (
                              <div key={key} className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">{label}</span>
                                <button
                                  onClick={() => updateSiteField(`settings.${key}`, !siteData.settings?.[key])}
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${siteData.settings?.[key] ? 'bg-primary-600' : 'bg-gray-200'
                                    }`}
                                >
                                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${siteData.settings?.[key] ? 'translate-x-5' : 'translate-x-0'
                                    }`} />
                                </button>
                              </div>
                            ))}
                            {siteData.settings?.enableLoyaltyProgram && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Loyalty Points Per £1</label>
                                <input
                                  type="number"
                                  value={siteData.settings?.loyaltyPointsPerPound || 10}
                                  onChange={(e) => updateSiteField('settings.loyaltyPointsPerPound', parseInt(e.target.value))}
                                  min="1"
                                  className="block w-48 border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Delivery Settings */}
                      <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                            <Truck className="w-5 h-5 text-gray-400" /> Delivery Settings
                          </h4>
                        </div>
                        <div className="px-5 py-5">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Radius (miles)</label>
                              <input
                                type="number"
                                value={siteData.settings?.deliveryRadius || 5}
                                onChange={(e) => updateSiteField('settings.deliveryRadius', parseInt(e.target.value))}
                                min="0"
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Delivery Order (£)</label>
                              <input
                                type="number"
                                value={siteData.settings?.minimumDeliveryOrder || 15}
                                onChange={(e) => updateSiteField('settings.minimumDeliveryOrder', parseFloat(e.target.value))}
                                step="0.50"
                                min="0"
                                className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Site Status */}
                      <div className="bg-white shadow rounded-lg overflow-hidden">
                        <div className="px-5 py-5">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-700">Site Status</p>
                              <p className="text-xs text-gray-500">Active sites are visible and accepting orders</p>
                            </div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${siteData.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                              {siteData.isActive ? '● Active' : '● Inactive'}
                            </span>
                          </div>
                          {siteData.isHeadOffice && (
                            <p className="mt-2 text-xs text-primary-600 font-medium">🏢 This is the Head Office</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
